import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from './ProductCard';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import { cn } from '@/lib/utils';

const MOBILE_PER_SLIDE = 2;
const DESKTOP_VISIBLE = 4;
const AUTO_MS = 4500;
const MIN_LOOP_SLIDES = 8;

const chunkProducts = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const useIsLg = () => {
  const [isLg, setIsLg] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsLg(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return isLg;
};

const ProductCarousel = ({
  title,
  eyebrow,
  products,
  viewAll = '/catalog',
}) => {
  const isLg = useIsLg();
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  // For Embla loop to work seamlessly on desktop, we need enough slides so
  // the total width exceeds 2× the viewport.  When the list is short (e.g. 7
  // items shown 4-at-a-time), duplicate the array so every product is still
  // reachable and the loop animation has no gap.
  const desktopProducts = useMemo(() => {
    if (!isLg || products.length === 0 || products.length >= MIN_LOOP_SLIDES) return products;
    // duplicate until we have enough
    const copies = Math.ceil(MIN_LOOP_SLIDES / products.length);
    const expanded = [];
    for (let c = 0; c < copies; c++) {
      products.forEach((p, i) => expanded.push({ ...p, _key: `${p.id || p.slug || i}-${c}` }));
    }
    return expanded;
  }, [isLg, products]);

  const mobileSlides = useMemo(
    () => chunkProducts(products, MOBILE_PER_SLIDE),
    [products],
  );
  const mobileCanNavigate = products.length > MOBILE_PER_SLIDE;
  const desktopCanNavigate = products.length > DESKTOP_VISIBLE;
  const canNavigate = isLg ? desktopCanNavigate : mobileCanNavigate;
  const slideCount = isLg
    ? desktopProducts.length
    : mobileSlides.length;

  const onSelect = useCallback(() => {
    if (!api) return;
    const snap = api.selectedScrollSnap();
    const count = slideCount || 1;
    setCurrent(((snap % count) + count) % count);
  }, [api, slideCount]);

  useEffect(() => {
    if (!api) return;
    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    api.reInit();
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api, onSelect, products.length, isLg, mobileSlides.length]);

  const stopAuto = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const startAuto = useCallback(() => {
    if (!api || !canNavigate) return;
    stopAuto();
    timerRef.current = setInterval(() => {
      api.scrollNext();
    }, AUTO_MS);
  }, [api, canNavigate, stopAuto]);

  useEffect(() => {
    startAuto();
    return stopAuto;
  }, [startAuto, stopAuto, current, isLg]);

  if (!products?.length) return null;

  return (
    <section
      className="py-8 sm:py-10 lg:py-7"
      onMouseEnter={stopAuto}
      onMouseLeave={startAuto}
    >
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-5 sm:mb-6 lg:mb-4 gap-4">
          <div>
            {eyebrow && (
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[var(--msa-green-dark)] mb-2">
                {eyebrow}
              </div>
            )}
            <h2 className="font-display text-[24px] sm:text-[32px] font-bold leading-tight">{title}</h2>
          </div>
          <Link
            to={viewAll}
            className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-[var(--msa-ink-soft)] hover:text-[var(--msa-green-dark)] transition-colors"
          >
            Дивитись всі <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="relative">
          <Carousel
            key={isLg ? 'desktop' : 'mobile'}
            setApi={setApi}
            opts={{
              align: 'start',
              loop: canNavigate,
              containScroll: canNavigate ? false : 'trimSnaps',
              slidesToScroll: 1,
              dragFree: false,
            }}
            className="w-full touch-pan-x"
          >
            <CarouselContent className="-ml-2 sm:-ml-3 lg:-ml-4 touch-pan-x items-stretch">
              {isLg
                ? desktopProducts.map((p, i) => (
                    <CarouselItem
                      key={p._key || p.id || p.slug || i}
                      className="pl-2 sm:pl-3 lg:pl-4 basis-1/4 flex"
                    >
                      <ProductCard
                        compact
                        className="w-full"
                        product={{ ...p, id: p.slug || p.id }}
                        priority={i < 4}
                      />
                    </CarouselItem>
                  ))
                : mobileSlides.map((slideProducts, slideIdx) => (
                    <CarouselItem key={slideIdx} className="pl-3 sm:pl-5 basis-full min-w-0 flex">
                      <div className="grid grid-cols-2 gap-3 sm:gap-5 items-stretch w-full">
                        {slideProducts.map((p, i) => (
                          <ProductCard
                            compact
                            key={p.id || p.slug}
                            className="w-full"
                            product={{ ...p, id: p.slug || p.id }}
                            priority={slideIdx === 0 && i < 2}
                          />
                        ))}
                      </div>
                    </CarouselItem>
                  ))}
            </CarouselContent>
          </Carousel>

          {canNavigate && (
            <>
              <button
                type="button"
                onClick={() => {
                  api?.scrollPrev();
                  startAuto();
                }}
                className="absolute left-1 sm:left-0 top-[42%] -translate-y-1/2 z-10 flex w-9 h-9 sm:w-11 sm:h-11 items-center justify-center rounded-full bg-white border border-[var(--msa-line)] shadow-md text-[var(--msa-ink)] hover:border-[var(--msa-green)] hover:text-[var(--msa-green-dark)] transition-colors sm:-translate-x-1/2"
                aria-label="Попередні товари"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => {
                  api?.scrollNext();
                  startAuto();
                }}
                className="absolute right-1 sm:right-0 top-[42%] -translate-y-1/2 z-10 flex w-9 h-9 sm:w-11 sm:h-11 items-center justify-center rounded-full bg-white border border-[var(--msa-line)] shadow-md text-[var(--msa-ink)] hover:border-[var(--msa-green)] hover:text-[var(--msa-green-dark)] transition-colors sm:translate-x-1/2"
                aria-label="Наступні товари"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {canNavigate && !isLg && (
          <div className="flex items-center justify-center gap-2 mt-6">
            {mobileSlides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  api?.scrollTo(i);
                  startAuto();
                }}
                className={cn(
                  'h-2 rounded-full transition-all shrink-0',
                  i === current
                    ? 'w-6 bg-[var(--msa-green-dark)]'
                    : 'w-2 bg-[var(--msa-line)] hover:bg-[var(--msa-green)]'
                )}
                aria-label={`Слайд ${i + 1}`}
                aria-current={i === current ? 'true' : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductCarousel;
